<template>
  <div class="staffing-heatmap" data-testid="month-table">
    <div class="table-header-row">
      <h3 class="h4 card-secondary-header">
        <div @click="onMonthClick">Months</div>
        <div class="separator" v-if="dayData.length > 0">&nbsp;/&nbsp;</div>
        <div v-if="dayData.length > 0" @click="hourData = []">
          {{ monthString }}
        </div>
        <div class="separator" v-if="hourData.length > 0">&nbsp;/&nbsp;</div>
        <div v-if="hourData.length > 0">{{ dayString }}</div>
      </h3>
    </div>
    <ag-grid-vue
      class="ag-theme-alpine"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :groupDisplayType="'groupRows'"
      :groupDefaultExpanded="1"
      :groupRowRendererParams="groupRowRendererParams"
      :suppressColumnVirtualisation="true"
      :defaultColDef="defaultColDef"
      @grid-ready="onGridReady"
      @newColumnsLoaded="newColumnsLoaded"
    >
    </ag-grid-vue>
    <div class="colour-guide-wrapper">
      <div>Under</div>
      <div class="colour-guide"></div>
      <div>Over</div>
    </div>
  </div>
</template>

<script>
import "ag-grid-enterprise";
import { AgGridVue } from "ag-grid-vue";
import {
  Translator,
  CapacityPlannerMonthlyView,
} from "@agyletime/wfm-vue-common";
import DateHeader from "../charts/DateHeader.vue";
import { utcToZonedTime } from "date-fns-tz";
import { getLeave, round, getAvailable } from "../../utils/math";
import {
  getBestTextColourForColour,
  getOpacityEquivalent,
} from "../../utils/colour";
import { reduce, max, map, flatten, prop, pipe, min } from "ramda";

export default {
  name: "StaffingHeatmap",
  mixins: [Translator],
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    agColumnHeader: DateHeader,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    monthData: {
      type: Array,
      required: true,
    },
    workloadTasks: {
      type: Map,
      required: true,
    },
  },
  watch: {
    columnDefs() {
      this.newColumnsLoaded();
    },
  },
  data() {
    return {
      dayData: [],
      hourData: [],
      loading: false,
    };
  },
  computed: {
    selectedMonth() {
      if (this.dayData?.length > 0) {
        return this.dayData[0].date;
      }
      return null;
    },
    monthString() {
      return this.getValueInLocale(
        utcToZonedTime(this.selectedMonth, this.timezone),
        {
          month: "long",
        }
      );
    },
    selectedDay() {
      if (this.hourData?.length > 0) {
        return this.hourData[0].date;
      }
      return null;
    },
    dayString() {
      return this.getValueInLocale(
        utcToZonedTime(this.selectedDay, this.timezone),
        {
          day: "2-digit",
        }
      );
    },
    timezone() {
      return this.configuration.timezone;
    },
    menuTabs() {
      return [];
    },
    defaultColDef() {
      if (this.hourData.length > 0) {
        return { flex: 1, minWidth: 40, resizable: true };
      } else if (this.dayData.length > 0) {
        return { flex: 1, minWidth: 40, resizable: true };
      } else {
        return { flex: 1, minWidth: 100, resizable: true };
      }
    },
    minValue() {
      const getValuesFromRowDataFormat = pipe(map(prop("times")), flatten);
      const values = getValuesFromRowDataFormat(this.rowData);
      return reduce(min, Infinity, values || []);
    },
    maxValue() {
      const getValuesFromRowDataFormat = pipe(map(prop("times")), flatten);
      const values = getValuesFromRowDataFormat(this.rowData);
      return reduce(max, -Infinity, values || []);
    },
    valueOrZero() {
      const prop = this.unitOfWorkProp;
      return (value) => {
        if (value[prop]) {
          return value[prop];
        } else {
          return 0;
        }
      };
    },
    unitOfWorkProp() {
      return this.configuration.unitOfWork === "FTE" ? "fte" : "hours";
    },
    groupRowRendererParams() {
      return {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        suppressEnterExpand: true,
      };
    },
    defaultColumns() {
      return [
        {
          headerName: this.translate("workload-task-name"),
          field: "type",
          menuTabs: this.menuTabs,
          width: 130,
          flex: 0,
          resizable: false,
          suppressSizeToFit: true,
          pinned: "left",
          type: "rightAligned",
          cellStyle: {
            // "font-weight": "bold",
          },
        },
      ];
    },
    standardColumnOptions() {
      return {
        cellStyle: this.getStyleForCellValue,
        menuTabs: this.menuTabs,
        type: "numericColumn",
        flex: 1,
        isDate: true,
      };
    },
    monthColumns() {
      return this.monthData?.map((month, index) => {
        return {
          ...this.standardColumnOptions,
          valueGetter: (params) => {
            if (!params.data) {
              return "";
            }
            return round(params.data?.times[index], 1);
          },
          headerName: this.getValueInLocale(
            utcToZonedTime(month.date, this.timezone),
            {
              month: "short",
            }
          ),
          click: () => {
            this.getMonthData(month.date);
          },
        };
      });
    },
    dayColumns() {
      return this.dayData?.map((day, index) => {
        return {
          ...this.standardColumnOptions,
          valueGetter: (params) => {
            if (!params.data) {
              return "";
            }
            return round(params.data?.times[index], 1);
          },
          headerName: this.getValueInLocale(
            utcToZonedTime(day.date, this.timezone),
            {
              day: "2-digit",
            }
          ),
          click: () => {
            this.getDayData(day.date);
          },
        };
      });
    },
    hourColumns() {
      return this.hourData?.map((hour, index) => {
        return {
          ...this.standardColumnOptions,
          valueGetter: (params) => {
            if (!params.data) {
              return "";
            }
            return round(params.data?.times[index], 1);
          },
          headerName: this.getValueInLocale(
            utcToZonedTime(hour.date, this.timezone),
            {
              hour: "2-digit",
            }
          ),
          isDate: false,
        };
      });
    },
    columnDefs() {
      if (this.hourData.length > 0) {
        return [...this.defaultColumns, ...this.hourColumns];
      } else if (this.dayData.length > 0) {
        return [...this.defaultColumns, ...this.dayColumns];
      } else {
        return [...this.defaultColumns, ...this.monthColumns];
      }
    },
    uniqueWorkloads() {
      const workloadsForAllDay = this.monthData
        .flatMap((day) => day.workloadTasks)
        .filter(Boolean);

      const uniqueWorkloadsById = workloadsForAllDay.reduce((acc, task) => {
        acc.set(task.workloadTaskId + "" + task.isAdjustment, task);
        return acc;
      }, new Map());

      let uniqueWorkloads = this.hydrateWorkloadsWithTask(
        Array.from(uniqueWorkloadsById.values())
      );
      if (uniqueWorkloads.some((workload) => workload.isAdjustment === true)) {
        uniqueWorkloads = uniqueWorkloads.map((workload) => {
          let nameAmendment = this.translate(
            "staffing-row-staffing-actual-title"
          );
          if (workload.isAdjustment) {
            nameAmendment = this.translate(
              "staffing-row-staffing-adjustment-title"
            );
          }
          workload.task = {
            ...workload.task,
            name: workload.task.name + nameAmendment,
          };
          return workload;
        });
      }
      // sort by name prop
      const sortedWorloads = uniqueWorkloads.sort((a, b) => {
        return a.task.name.localeCompare(b.task.name);
      });

      return sortedWorloads;
    },
    rowDataForCurrentSelection() {
      if (this.hourData?.length > 0) {
        return this.hydrateCapacityWithTasks(this.hourData);
      } else if (this.dayData?.length > 0) {
        return this.hydrateCapacityWithTasks(this.dayData);
      } else {
        return this.hydrateCapacityWithTasks(this.monthData);
      }
    },
    rowData() {
      const workloads = this.uniqueWorkloads.map((workload) => ({
        task: workload,
        type: workload.task.name,
        times: this.getDataForWorkloadForDays(
          workload,
          "difference",
          (task) => {
            const leave = getLeave(
              task?.plannedLeave?.[this.unitOfWorkProp],
              task?.unplannedLeave?.[this.unitOfWorkProp]
            );
            const available = getAvailable(
              task?.available?.[this.unitOfWorkProp],
              leave,
              task?.shrinkage?.[this.unitOfWorkProp]
            );
            const required = round(
              task?.required?.[this.unitOfWorkProp] || 0,
              1
            );
            return available - required;
          }
        ),
      }));

      const total = {
        type: "Total",
        task: null,
        times: this.rowDataForCurrentSelection.map((r) => {
          const leave = getLeave(
            r?.plannedLeave?.[this.unitOfWorkProp],
            r?.unplannedLeave?.[this.unitOfWorkProp]
          );
          const available = getAvailable(
            r?.available?.[this.unitOfWorkProp],
            leave,
            r?.shrinkage?.[this.unitOfWorkProp]
          );
          const required = round(r?.required?.[this.unitOfWorkProp] || 0, 1);
          return available - required;
        }),
      };

      return [...workloads, total];
    },
  },
  methods: {
    onMonthClick() {
      if (this.dayData?.length > 0) {
        this.dayData = [];
        this.hourData = [];
      }
    },
    onDayClick() {
      if (this.hourData?.length > 0) {
        this.hourData = [];
      }
    },
    // async getYearData() {
    //   this.monthData = await CapacityPlannerMonthlyView.getConfiguration(id);
    // },
    getStyleForCellValue(params) {
      const violet = [100, 96, 163];
      const red = [232, 90, 90];

      if (params.value > 0) {
        const opacity = params.value / this.maxValue;
        const backgroundColour = getOpacityEquivalent(
          violet[0],
          violet[1],
          violet[2],
          opacity
        );
        const colour = getBestTextColourForColour(...backgroundColour);

        return {
          backgroundColor: `rgb(${backgroundColour.join(",")})`,
          color: `rgb(${colour.join(",")})`,
        };
      } else if (params.value < 0) {
        const opacity = params.value / this.minValue;
        const backgroundColour = getOpacityEquivalent(
          red[0],
          red[1],
          red[2],
          opacity
        );
        const colour = getBestTextColourForColour(...backgroundColour);
        return {
          backgroundColor: `rgb(${backgroundColour.join(",")})`,
          color: `rgb(${colour.join(",")})`,
        };
      }
      return null;
    },
    getTaskValue(task, prop) {
      const value = task?.[prop]?.[this.unitOfWorkProp] || 0;
      return value ? value.toFixed(1) : 0;
    },
    getDataForWorkloadForDays(workload, prop, calc) {
      return this.rowDataForCurrentSelection.map((timeData) => {
        const workloadTask = timeData.workloadTasks?.find(
          (task) =>
            task.workloadTaskId === workload.workloadTaskId &&
            task.isAdjustment === workload.isAdjustment
        );

        return calc ? calc(workloadTask) : this.getTaskValue(timeData, prop);
      });
    },
    handleLoading() {
      if (this.gridApi) {
        if (this.loading || this.monthData.length === 0) {
          this.gridApi.showLoadingOverlay();
        } else {
          this.gridApi.hideOverlay();
        }
      }
    },
    sizeToFit() {
      this.gridColumnApi?.autoSizeAllColumns();
    },
    newColumnsLoaded() {
      this.sizeToFit();
      this.handleLoading();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.gridApi.setDomLayout("autoHeight");
      this.sizeToFit();
    },
    hydrateCapacityWithTasks(data) {
      return data.map((timeData) => {
        return {
          ...timeData,
          workloadTasks: timeData.workloadTasks?.map((task) => {
            return {
              ...task,
              task: this.workloadTasks.get(task.workloadTaskId),
            };
          }),
        };
      });
    },
    hydrateWorkloadsWithTask(workloads) {
      return workloads.map((workload) => {
        return {
          ...workload,
          task: this.workloadTasks.get(workload.workloadTaskId),
        };
      });
    },
    toggleLoading() {
      this.loading = !this.loading;
      this.handleLoading();
    },
    async getMonthData(startDate) {
      this.toggleLoading();
      this.dayData = await CapacityPlannerMonthlyView.getDailyCapacityForMonth(
        this.configuration._id,
        startDate
      );
      this.toggleLoading();
    },
    async getDayData(startDate) {
      this.toggleLoading();
      this.hourData = await CapacityPlannerMonthlyView.getHourlyCapacityForDay(
        this.configuration._id,
        startDate
      );
      this.toggleLoading();
    },
  },
};
</script>

<style lang="scss">
@import "ag-grid-community/dist/styles/ag-grid.css";
@import "ag-grid-community/dist/styles/ag-theme-alpine.css";

.table-header-row {
  margin-bottom: 1rem;

  display: flex;

  & > * {
    flex: 1;

    :last-child:not(:first-child) {
      font-weight: bold;
    }

    :not(:last-child) {
      text-decoration: underline;
      cursor: pointer;
    }

    .separator {
      text-decoration: none;
    }
  }
}
/* Hide the collapse chevron */
.ag-root-wrapper .ag-row > .ag-row-group > .ag-group-expanded {
  display: none;
}

.card-secondary-header {
  display: flex;
}

.colour-guide-wrapper {
  margin-top: 0.5em;
  display: flex;
  align-items: center;
}

.colour-guide {
  margin-left: 0.5em;
  margin-right: 0.5em;
  width: 8em;
  height: 1em;
  background: linear-gradient(
    90deg,
    var(--color-red--salsa-50),
    #ffffff,
    var(--color-deep-violet-50)
  );
  border: 1px solid var(--color-black--eerie-50);
}

// Remove min height constraint mentioned in AG-Grid docs
// https://www.ag-grid.com/javascript-data-grid/grid-size/#example-auto-height
.ag-center-cols-clipper {
  min-height: unset !important;
}
</style>
